/**
 * Slideshow [New] — vanilla JS carousel
 * Matches VG-react Hero behavior (fade + Ken Burns + progress bars)
 */
(function () {
  'use strict';

  var SECTION_TYPE = 'slideshow-new';
  var instances = new WeakMap();

  function pad2(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function SlideshowNew(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('.vg-slideshow-new__slide'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('.vg-slideshow-new__panel'));
    this.bars = Array.prototype.slice.call(root.querySelectorAll('.vg-slideshow-new__bar'));
    this.counterEl = root.querySelector('[data-counter-current]');
    this.index = 0;
    this.timer = null;
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.speed = parseInt(root.getAttribute('data-autoplay-speed'), 10) || 7000;
    this.pauseOnHover = root.getAttribute('data-pause-on-hover') === 'true';
    this.paused = false;
    this.reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.slides.length === 0) return;

    root.style.setProperty('--vg-ss-kenburns-duration', this.speed + 'ms');
    this.bind();
    this.goTo(0, { restart: true, instant: true });
  }

  SlideshowNew.prototype.bind = function () {
    var self = this;

    this.bars.forEach(function (bar) {
      bar.addEventListener('click', function () {
        var i = parseInt(bar.getAttribute('data-goto'), 10);
        if (!isNaN(i)) self.goTo(i, { restart: true });
      });
    });

    if (this.pauseOnHover) {
      this.root.addEventListener('mouseenter', function () {
        self.pause();
      });
      this.root.addEventListener('mouseleave', function () {
        self.resume();
      });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
      else self.resume();
    });
  };

  SlideshowNew.prototype.clearTimer = function () {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  };

  SlideshowNew.prototype.schedule = function () {
    var self = this;
    this.clearTimer();
    if (!this.autoplay || this.slides.length < 2 || this.paused || this.reducedMotion) return;
    this.timer = setTimeout(function () {
      self.goTo(self.index + 1, { restart: true });
    }, this.speed);
  };

  SlideshowNew.prototype.pause = function () {
    if (this.paused) return;
    this.paused = true;
    this.clearTimer();
    this.bars.forEach(function (bar) {
      if (bar.classList.contains('is-active')) bar.classList.add('is-paused');
    });
  };

  SlideshowNew.prototype.resume = function () {
    if (!this.paused) return;
    this.paused = false;
    this.bars.forEach(function (bar) {
      bar.classList.remove('is-paused');
    });
    /* Restart active bar animation + timer from the beginning for reliable timing */
    this.refreshBars(this.index);
    this.schedule();
  };

  SlideshowNew.prototype.transitionPanels = function (prev, target, instant) {
    var self = this;
    /* Hero.jsx: transition={{ duration: 0.9 }} + AnimatePresence mode="wait" */
    var TEXT_MS = 900;

    if (this.reducedMotion) instant = true;

    if (this._panelTimer) {
      clearTimeout(this._panelTimer);
      this._panelTimer = null;
    }

    this.panels.forEach(function (panel) {
      panel.classList.remove('is-prep');
    });

    if (instant || prev === target) {
      this.panels.forEach(function (panel, i) {
        panel.classList.remove('is-active', 'is-exiting', 'is-prep');
        if (i === target) panel.classList.add('is-active');
      });
      return;
    }

    var outgoing = this.panels[prev];
    var incoming = this.panels[target];

    /* Reset any panels not in this transition */
    this.panels.forEach(function (panel) {
      if (panel !== outgoing && panel !== incoming) {
        panel.classList.remove('is-active', 'is-exiting', 'is-prep');
      }
    });

    /* Keep incoming parked at initial { opacity: 0, y: 30 } while exit plays */
    if (incoming) {
      incoming.classList.remove('is-active', 'is-exiting');
      incoming.classList.add('is-prep');
      void incoming.offsetWidth;
      incoming.classList.remove('is-prep');
    }

    /* 1) Exit current — { opacity: 0, y: -20 } over 900ms */
    if (outgoing) {
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-exiting');
    }

    /* 2) After exit completes, enter next — mode="wait" */
    this._panelTimer = setTimeout(function () {
      if (outgoing) {
        outgoing.classList.remove('is-exiting');
      }

      if (incoming) {
        /* Snap to initial, then animate to active (same as Framer remount) */
        incoming.classList.add('is-prep');
        incoming.classList.remove('is-active', 'is-exiting');
        void incoming.offsetWidth;
        incoming.classList.remove('is-prep');
        void incoming.offsetWidth;
        incoming.classList.add('is-active');
      }

      self._panelTimer = null;
    }, TEXT_MS);
  };

  SlideshowNew.prototype.refreshBars = function (activeIndex) {
    this.bars.forEach(function (bar, i) {
      var fill = bar.querySelector('.vg-slideshow-new__bar-fill');
      bar.classList.remove('is-active', 'is-complete', 'is-paused');
      bar.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
      if (fill) {
        /* restart CSS animation */
        fill.style.animation = 'none';
        // force reflow
        void fill.offsetWidth;
        fill.style.animation = '';
      }
      if (i < activeIndex) {
        bar.classList.add('is-complete');
      } else if (i === activeIndex) {
        bar.classList.add('is-active');
      }
    });
  };

  SlideshowNew.prototype.applySlideTheme = function (target) {
    var panel = this.panels[target];
    if (!panel) return;

    var align = panel.getAttribute('data-text-align') || 'left';
    this.root.classList.remove(
      'vg-slideshow-new--align-left',
      'vg-slideshow-new--align-center',
      'vg-slideshow-new--align-right'
    );
    this.root.classList.add('vg-slideshow-new--align-' + align);

    var mobileBelow = panel.getAttribute('data-mobile-below') === 'true';
    this.root.classList.toggle('vg-slideshow-new--mobile-text-below', mobileBelow);

    var slideBg = panel.getAttribute('data-slide-bg');
    if (slideBg) this.root.style.setProperty('--vg-ss-base', slideBg);

    var mobileMediaH = panel.getAttribute('data-mobile-media-h');
    if (mobileMediaH) this.root.style.setProperty('--vg-ss-mobile-media-h', mobileMediaH + 'svh');

    var mobileBelowBg = panel.getAttribute('data-mobile-below-bg');
    if (mobileBelowBg) this.root.style.setProperty('--vg-ss-mobile-below-bg', mobileBelowBg);

    var mobileBelowText = panel.getAttribute('data-mobile-below-text');
    if (mobileBelowText) this.root.style.setProperty('--vg-ss-mobile-below-text', mobileBelowText);
  };

  SlideshowNew.prototype.goTo = function (next, opts) {
    opts = opts || {};
    var total = this.slides.length;
    if (total === 0) return;

    var target = ((next % total) + total) % total;
    var prev = this.index;

    this.slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === target);
    });

    this.transitionPanels(prev, target, opts.instant);

    this.index = target;

    this.applySlideTheme(target);

    if (this.counterEl) {
      this.counterEl.textContent = pad2(target + 1);
    }

    if (opts.restart !== false) {
      this.refreshBars(target);
    }

    this.schedule();
  };

  SlideshowNew.prototype.destroy = function () {
    this.clearTimer();
    if (this._panelTimer) {
      clearTimeout(this._panelTimer);
      this._panelTimer = null;
    }
  };

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    var instance = new SlideshowNew(root);
    instances.set(root, instance);
  }

  function destroyRoot(root) {
    if (!root || !instances.has(root)) return;
    instances.get(root).destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll(
      '[data-section-type="' + SECTION_TYPE + '"]'
    );
    roots.forEach(initRoot);
  }

  function onSectionLoad(event) {
    var section = event.target;
    if (!section) return;
    var root = section.querySelector('[data-section-type="' + SECTION_TYPE + '"]');
    if (root) {
      destroyRoot(root);
      initRoot(root);
    } else if (section.matches && section.matches('[data-section-type="' + SECTION_TYPE + '"]')) {
      destroyRoot(section);
      initRoot(section);
    }
  }

  function onSectionUnload(event) {
    var section = event.target;
    if (!section) return;
    var root =
      section.querySelector('[data-section-type="' + SECTION_TYPE + '"]') ||
      (section.matches && section.matches('[data-section-type="' + SECTION_TYPE + '"]')
        ? section
        : null);
    if (root) destroyRoot(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  /* Theme editor events */
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:reorder', function () {
    initAll(document);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest('[data-section-type="' + SECTION_TYPE + '"]');
    if (!root || !instances.has(root)) return;
    var slide = event.target.closest('.vg-slideshow-new__slide');
    if (!slide) return;
    var i = parseInt(slide.getAttribute('data-slide-index'), 10);
    if (!isNaN(i)) instances.get(root).goTo(i, { restart: true });
  });

  /* In case defer script loads after DOMContentLoaded */
  if (document.readyState !== 'loading') {
    initAll(document);
  }
})();
